<!-- Background -->
<div class="fixed inset-0 employee-background -z-10 bg-gray-100"></div>

<!-- Top bar. The only chrome a restricted session gets, and the only exit it offers is signing out — the same
     arrangement as the forms-completion screen, for the same reason. -->
<div class="fixed inset-x-0 top-0 h-14 z-10 flex items-center px-4 sm:px-6 select-none bg-white/80 backdrop-blur-md border-b border-gray-200/60 shadow-sm">
  <img alt="CheqMe" class="h-9 rounded-full border border-gray-200 shadow-sm" src="assets/images/icon.png">
  <span class="ml-3 text-base lg:text-lg text-gray-900 fw-bold">CheqMe</span>
  <button (click)="logout()"
          class="ml-auto inline-flex items-center justify-center px-4 py-2 border border-gray-200 rounded-2xl text-gray-600 hover:bg-gray-50 hover:border-gray-300 font-semibold text-sm shadow-sm transition-all duration-200 active:scale-95">
    Abmelden
  </button>
</div>

<!-- Content -->
<div class="absolute inset-x-0 top-14 bottom-0 overflow-y-auto">
  <div class="max-w-4xl mx-auto px-4 sm:px-6 py-4 space-y-4">

    <!-- Intro / gate explanation -->
    <div class="p-4 bg-theme-primary-50/50 border border-theme-primary-100/50 rounded-2xl shadow-sm">
      <div class="flex items-start gap-3">
        <div class="p-2 bg-theme-primary-600 text-white rounded-xl shrink-0">
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"
               xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M8 10h8m-8 4h5m7-2a9 9 0 01-9 9 8.96 8.96 0 01-4.03-.95L3 21l1.02-3.86A8.96 8.96 0 013 12a9 9 0 1118 0z"
                  stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
          </svg>
        </div>
        <div class="min-w-0">
          <h1 class="text-xl font-bold text-gray-900 tracking-tight" data-test="contact-gate-heading">
            Kontakt verknüpfen
          </h1>
          @if (blocking()) {
            <p class="mt-1 text-sm text-gray-600 leading-relaxed">
              Bevor Sie fortfahren können, muss Ihr Konto mit den unten genannten Kontaktkanälen verknüpft
              werden. Sobald das erledigt ist, wird Ihr Zugang automatisch freigeschaltet.
            </p>
          } @else {
            <p class="mt-1 text-sm text-gray-600 leading-relaxed" data-test="contact-pending-intro">
              Diese Verknüpfungen sind für Sie vorgesehen. Sie können sie jetzt einrichten oder später. Ihr
              Zugang bleibt bis zum Ende der Frist uneingeschränkt.@if (pendingDeadline()) {<span
              class="font-semibold"> Frist bis {{ pendingDeadline() | date: 'dd.MM.yyyy' }}.</span>}
            </p>
          }
          @if (!loading() && !blockedNoAction() && total > 0 && currentTask && blocking()) {
            <p class="mt-2 text-xs font-bold text-theme-primary-700 uppercase tracking-widest"
               data-test="contact-progress">
              Kanal {{ (activeIndex() ?? 0) + 1 }} von {{ total }}
            </p>
          }

          @if (!blocking() && !loading()) {
            <button type="button" (click)="postpone()" data-test="contact-postpone"
                    class="mt-3 inline-flex items-center justify-center rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 active:scale-95">
              Später erledigen
            </button>
          }
        </div>
      </div>
    </div>

    <!-- Error -->
    @if (errorMessage()) {
      <div class="p-4 bg-red-50 border border-red-100 rounded-2xl shadow-sm flex items-start gap-3"
           data-test="contact-error">
        <svg class="h-5 w-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"
             xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
        </svg>
        <div class="min-w-0">
          <p class="text-sm font-semibold text-red-600">{{ errorMessage() }}</p>
          <button (click)="refresh()" data-test="contact-error-retry"
                  class="mt-2 inline-flex items-center text-red-600 bg-red-100 hover:bg-red-200 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200">
            Erneut versuchen
          </button>
        </div>
      </div>
    }

    <!-- Loading -->
    @if (loading()) {
      <div class="flex items-center justify-center py-20" data-test="contact-loading">
        <svg class="h-8 w-8 animate-spin text-theme-primary-500" fill="none" viewBox="0 0 24 24"
             xmlns="http://www.w3.org/2000/svg">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
        </svg>
      </div>
    }

    <!-- Blocked with nothing on offer — typically a required channel whose bot is not configured. Named
         explicitly rather than rendered as an empty list, because an empty lockout screen is a dead end. -->
    @if (!loading() && blockedNoAction()) {
      <div class="p-4 bg-white border border-gray-100 rounded-2xl shadow-sm text-center"
           data-test="contact-no-action">
        <p class="text-base font-bold text-gray-800 tracking-tight">Einrichtung ausstehend</p>
        <p class="mt-2 text-sm text-gray-500 leading-relaxed" data-test="contact-no-action-reason">
          {{ unavailableMessage }}
        </p>
        <p class="mt-2 text-sm text-gray-500 leading-relaxed">
          Sobald der Kanal bereitsteht, wird Ihr Zugang wieder freigeschaltet.
        </p>
      </div>
    }

    <!-- The task list. In the blocking mode the wizard is already open on the first task, so this is the
         voluntary mode's surface; while blocking it stays as the overview of what is still to come. -->
    @if (!loading() && !blockedNoAction() && total > 0 && !currentTask) {
      <ul class="flex flex-col gap-3" data-test="contact-tasks">
        @for (task of tasks(); track task.channel + ':' + task.purpose) {
          <li class="p-4 bg-white border border-gray-100 rounded-2xl shadow-sm">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div class="min-w-0">
                <p class="text-base font-bold text-gray-800 tracking-tight">{{ label(task.channel) }}</p>
                <p class="mt-1 text-sm text-gray-500 leading-relaxed">
                  @if (task.purpose === Purpose.Reverify) {
                    Bitte bestätigen Sie, dass diese Verknüpfung noch aktuell ist.
                  } @else {
                    Dieser Kanal ist noch nicht mit Ihrem Konto verknüpft.
                  }
                  @if (task.deadline) {
                    <span class="font-semibold">Frist bis {{ task.deadline | date: 'dd.MM.yyyy' }}.</span>
                  }
                </p>
              </div>
              <button type="button" (click)="open($index)" [attr.data-test]="'contact-task-start-' + $index"
                      class="shrink-0 inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-theme-primary-700 active:scale-95">
                @if (task.purpose === Purpose.Reverify) { Erneut bestätigen } @else { Jetzt verknüpfen }
              </button>
            </div>
          </li>
        }
      </ul>
    }

    <!-- The active task -->
    @if (!loading()) {
      @if (currentTask; as task) {
        <!-- `block`: a custom element renders inline by default, and the parent's `space-y-4` is a margin-top,
             which an inline box ignores — so without it the wizard card sat flush against the header card. -->
        <app-contact-link-wizard class="block" [channel]="task.channel" [channelLabel]="label(task.channel)"
                                 [purpose]="task.purpose" [dismissable]="!blocking()"
                                 (completed)="onTaskCompleted()" (dismissed)="cancel()"></app-contact-link-wizard>
      }
    }

  </div>
</div>
